import {
  UNABLE_TO_ASSESS_LABEL,
  VITAL_ROWS,
  VitalRow,
  VitalsValue,
  calculateBmi,
  getVitalError,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  NOTE_CONTENT_SECTION_KEYS,
  NOTE_BMI_ANSWER_KEY,
  NOTE_BMI_EMPTY,
  NOTE_BMI_LABEL,
  NOTE_BMI_UNIT,
  NOTE_FREE_TEXT_ANSWER_KEY,
  NOTE_VITAL_DETAIL_SEPARATOR,
} from "./initial-f2f-constant";
import {
  Answers,
  getAnswerKey,
  parseFreeText,
  toSection,
} from "./initial-f2f-note-answers";
import { NoteContentFields, NoteLine, NoteSection } from "./initial-f2f-types";

type VitalsFields = Pick<
  NoteContentFields,
  "vitalSigns" | "vitalSignsFreeText"
>;

const vitalLine = (label: string, unit: string, row: VitalRow): NoteLine => {
  if (!row.unable) return { title: label, text: `${row.value.trim()} ${unit}` };
  const note = row.note.trim();
  const detail = note ? `${NOTE_VITAL_DETAIL_SEPARATOR}${note}` : "";
  return { title: label, text: `${UNABLE_TO_ASSESS_LABEL}${detail}` };
};

export const buildVitalsSection = (
  fields: VitalsFields
): NoteSection | undefined => {
  const answers: Answers = {};
  const lines: NoteLine[] = [];
  VITAL_ROWS.forEach((def) => {
    const row = fields.vitalSigns[def.key];
    if (!row || (!row.value.trim() && !row.unable)) return;
    const note = row.note.trim();
    answers[getAnswerKey(def.key)] = {
      value: row.value.trim(),
      ...(row.unable && { unable: true }),
      ...(note && { note }),
    };
    lines.push(vitalLine(def.label, def.unit, row));
  });
  const bmi = calculateBmi(
    fields.vitalSigns.height?.value ?? "",
    fields.vitalSigns.weight?.value ?? ""
  );
  if (bmi !== NOTE_BMI_EMPTY) {
    answers[NOTE_BMI_ANSWER_KEY] = { value: bmi };
    lines.push({ title: NOTE_BMI_LABEL, text: `${bmi} ${NOTE_BMI_UNIT}` });
  }
  const freeText = fields.vitalSignsFreeText.trim();
  if (freeText) {
    answers[NOTE_FREE_TEXT_ANSWER_KEY] = { value: freeText };
    lines.push({ text: freeText });
  }
  return toSection(
    answers,
    lines.map((line) =>
      line.title ? `${line.title}: ${line.text}` : line.text
    ),
    lines
  );
};

export const parseVitalsSection = (answers: Answers): VitalsFields => ({
  vitalSigns: VITAL_ROWS.reduce<VitalsValue>((vitals, def) => {
    const answer = answers[getAnswerKey(def.key)];
    if (!answer) return vitals;
    vitals[def.key] = {
      value: typeof answer.value === "string" ? answer.value : "",
      unable: !!answer.unable,
      note: answer.note ?? "",
    };
    return vitals;
  }, {}),
  vitalSignsFreeText: parseFreeText(answers),
});

/** Labels of entered vitals that break their range or format; "unable to assess" rows are skipped. */
export const findInvalidVitals = (
  noteContent?: Record<string, object>
): string[] => {
  const sections = (noteContent?.sections ?? {}) as Record<
    string,
    NoteSection | undefined
  >;
  const { vitalSigns } = parseVitalsSection(
    sections[NOTE_CONTENT_SECTION_KEYS.vitalSigns]?.answers ?? {}
  );
  return VITAL_ROWS.filter((def) => {
    const row = vitalSigns[def.key];
    return !!row && !row.unable && !!getVitalError(def, row.value);
  }).map((def) => def.label);
};
